import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import Sidebar from "./Sidebar";
import Topbar from "./Topbar";
import { getProfileData, getPendingRequestsThunk, getCheckinNotificationsThunk } from "../../redux/thunk";
import { useMediaQuery } from "../../utils/useMediaQuery";
import { useAppearance } from "../../utils/appearance";

const PAGE_TITLES = {
  "/home":          "Patients Health Overview",
  "/doctor-home":   "Patients Health Overview",
  "/addpatient":    "Invite Patients to Cuore",
  "/notifications": "Notifications",
  "/requests":      "Requests",
  "/verification":  "Registration & Fees",
  "/editprofile":   "My Profile",
  "/patientrecord": "Patient Record",
  "/onboarding":    "Onboarding",
  "/cuorecare":     "CuoreCare",
  "/cuorecare/availability": "Availability",
  "/cuorecare/history": "CuoreCare History",
  "/cuorecare/patients/new": "Add Patient",
  "/emr/patients/new": "Add Patient", // legacy alias, same page
  "/diagnostics":   "Diagnostics",
  "/analytics":     "Analytics",
  "/labresults":    "Diagnostics", // legacy path, still mounted — same page, same title
  "/settings":      "Settings",
};

export default function Layout({ children }) {
  const dispatch = useDispatch();
  const location = useLocation();
  const profileData = useSelector((s) => s.profile?.data);
  const pendingItems = useSelector((s) => s.pendingRequests?.items ?? []);
  const checkinItems = useSelector((s) => s.pendingRequests?.checkinItems ?? []);
  const notificationCount = pendingItems.length + checkinItems.length;

  const isLg = useMediaQuery("(min-width: 1024px)");
  const [collapsed, setCollapsed] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  /* Theme lives in utils/appearance.js so this toggle and the Settings page cannot
     disagree about it; the document class is applied there too. */
  const { dark: darkMode, setDark: setDarkMode } = useAppearance();

  /* `profileData` was a dependency here, so the moment the profile landed this effect ran
     again and re-fetched BOTH notification endpoints — against a backend whose median
     response is over two seconds. Every one of those is a request the doctor waits behind.
     The profile fetch is the only part that cares whether we already have it, and it guards
     itself; the two polls belong on mount. */
  useEffect(() => {
    dispatch(getPendingRequestsThunk());
    dispatch(getCheckinNotificationsThunk());
  }, [dispatch]);

  useEffect(() => {
    if (!profileData) dispatch(getProfileData());
    // Intentionally not re-running when profileData arrives — that is the point.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (isLg) setMobileNavOpen(false);
  }, [isLg]);

  const title =
    PAGE_TITLES[location.pathname] ??
    (location.pathname.startsWith("/cuorecare/consultation") ? "Consultation" :
     // "/patientrecord/:id" is the deep-linkable form of the record page; the exact-match
     // lookup above only covers the bare "/patientrecord", so match the prefix too.
     location.pathname.startsWith("/patientrecord") ? "Patient Record" : "Cuore");

  /* rem, not px: the sidebar has to grow with the text inside it, or a doctor on
     "Extra large" gets the same 240px rail with every label truncated. */
  const mainMarginLg = collapsed ? "4.5rem" : "15rem";

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex">
      {!isLg && mobileNavOpen && (
        <button
          type="button"
          aria-label="Close menu"
          className="fixed inset-0 z-40 bg-slate-900/50 backdrop-blur-[1px] lg:hidden"
          onClick={() => setMobileNavOpen(false)}
        />
      )}
      <Sidebar
        isLg={isLg}
        mobileNavOpen={mobileNavOpen}
        setMobileNavOpen={setMobileNavOpen}
        collapsed={collapsed}
        setCollapsed={setCollapsed}
        profileData={profileData}
      />
      <div
        className="flex-1 flex flex-col min-h-screen min-w-0 transition-[margin] duration-300 ease-in-out ml-0"
        style={{ marginLeft: isLg ? mainMarginLg : 0 }}
      >
        <Topbar
          isLg={isLg}
          mobileNavOpen={mobileNavOpen}
          setMobileNavOpen={setMobileNavOpen}
          collapsed={collapsed}
          profileData={profileData}
          pendingCount={notificationCount}
          darkMode={darkMode}
          setDarkMode={setDarkMode}
          title={title}
        />
        {/* Spacer pushes content below the fixed 64px topbar */}
        <div className="h-16 shrink-0" />
        {/* 300ms with an 8px slide was a THIRD OF A SECOND of deliberate delay on every
            navigation, on top of whatever the screen itself cost — and the slide meant the
            text was still moving when it became readable, which reads as sluggish even when
            nothing is actually slow. A short opacity-only fade keeps the transition from
            being a hard cut without standing between the doctor and the screen. */}
        <motion.main
          key={location.pathname}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.12, ease: "linear" }}
          className="flex-1 p-3 sm:p-4 md:p-6 overflow-y-auto max-w-full min-w-0"
        >
          {children}
        </motion.main>
      </div>
    </div>
  );
}
